import { actions, assign, Machine } from 'xstate';
import { logDetails } from '@trqk/utils';

import { getTask } from './utils';

const UNKNOWN_STATUS = 'Unknown';

const { log } = actions;

/**
 * Log out the tasks from context, as well as the event.
 */
export const logTasks = log(
    /* eslint-disable @typescript-eslint/no-unused-vars */
    /* eslint-disable @typescript-eslint/no-explicit-any */
    (context: TaskContext, event: any): string =>
        `tasks: ${JSON.stringify(
            Array.from(context.tasks.values()),
            null,
            4,
        )}\nEvent: ${JSON.stringify(event, null, 4)}\n-------------`,
    /* eslint-enable @typescript-eslint/no-unused-vars */
    /* eslint-enable @typescript-eslint/no-explicit-any */
);

/**
 * A state machine that manages asynchronous processes, or 'tasks'. Each task belongs to a group of
 * one or more tasks. This allows you to trigger a callback when all tasks within a group have
 * stopped running, due to either success, cancellation, or an error.
 *
 * Tasks will still exist in context until the entire group stops. After invoking the
 * `onGroupStopped` callback (if it exists), the machine then deletes all the tasks within the
 * group. The only exception to this pattern is if the `CANCEL_ALL_TASKS` event is called, which
 * will cancel and delete all tasks.
 */
export const taskMachine = Machine<TaskContext, TaskStateSchema, TaskEvent>(
    {
        id: 'taskMachine',
        type: 'compound',
        initial: 'running',
        context: {
            tasks: new Map(),
        },
        states: {
            running: {
                on: {
                    CREATE_GROUP: {
                        actions: ['createGroup'],
                    },
                    CANCEL: {
                        actions: ['cancel', 'checkIfGroupStopped'],
                    },
                    ERROR: {
                        actions: ['error', 'checkIfGroupStopped'],
                    },
                    SUCCESS: {
                        actions: ['success', 'checkIfGroupStopped'],
                    },
                    PROGRESS: {
                        actions: ['progress'],
                    },
                    STATUS: {
                        actions: ['status'],
                    },
                    CANCEL_ALL_TASKS: {
                        actions: ['cancelAllTasks'],
                    },
                },
            },
        },
    },
    {
        actions: {
            logDetails,
            logTasks,
            /**
             * Create a group of one or more tasks.
             */
            createGroup: assign((context: TaskContext, event: TaskEvent) => {
                if (event.type !== 'CREATE_GROUP') {
                    return context;
                }

                const {
                    value: {
                        groupId,
                        onGroupStart,
                        onGroupStopped,
                        onTaskCancel,
                        onTaskUpdate,
                        taskCreators,
                    },
                } = event;

                const pendingTasks = taskCreators.map(
                    ({
                        taskId,
                        input,
                        onTaskStart,
                        status,
                    }: TaskCreator<Record<string, unknown>>): UnknownTask => ({
                        taskId,
                        canceled: false,
                        done: false,
                        error: null,
                        groupId,
                        input,
                        onGroupStart,
                        onGroupStopped,
                        onTaskCancel,
                        onTaskStart,
                        onTaskUpdate,
                        paused: false,
                        progress: 0,
                        status: status ?? UNKNOWN_STATUS,
                        success: false,
                    }),
                );

                if (onGroupStart) {
                    onGroupStart(pendingTasks);
                }

                pendingTasks.forEach((newTask: UnknownTask): void => {
                    if (newTask.onTaskStart) {
                        /**
                         * If the task has an `onTaskStart`, this function will be passed to the
                         * `onTaskStart` method as an argument. This allows the `onTaskStart`
                         * function to bind an `onTaskCancel` callback to the task.
                         * */
                        const bindOnTaskCancel = (
                            onTaskCancelCallback: OnTaskCancel,
                        ): void => {
                            // eslint-disable-next-line no-param-reassign
                            newTask.onTaskCancel = onTaskCancelCallback;
                        };

                        newTask.onTaskStart(bindOnTaskCancel);
                    }

                    context.tasks.set(newTask.taskId, newTask);
                });

                return context;
            }),
            /**
             * Cancel a task and update its output
             */
            cancel: assign((context: TaskContext, event: TaskEvent) => {
                if (event.type !== 'CANCEL') {
                    return context;
                }

                const task = getTask(context, event);

                if (task === null) {
                    return context;
                }

                const nextTask = {
                    ...task,
                    output: event?.value?.output,
                    canceled: true,
                    done: true,
                };

                if (task.onTaskCancel) {
                    task.onTaskCancel(nextTask);
                }

                if (task.onTaskUpdate) {
                    task.onTaskUpdate(nextTask);
                }

                context.tasks.set(task.taskId, nextTask);

                return context;
            }),
            /**
             * Log an error for a task and update its output
             */
            error: assign((context: TaskContext, event: TaskEvent) => {
                if (event.type !== 'ERROR') {
                    return context;
                }

                const task = getTask(context, event);

                if (task === null) {
                    return context;
                }

                const nextTask = {
                    ...task,
                    output: event?.value?.output,
                    error: event.value.error,
                    paused: true,
                };

                if (task.onTaskUpdate) {
                    task.onTaskUpdate(nextTask);
                }

                context.tasks.set(task.taskId, nextTask);

                return context;
            }),
            /**
             * Log success for a task and update its output
             */
            success: assign((context: TaskContext, event: TaskEvent) => {
                if (event.type !== 'SUCCESS') {
                    return context;
                }

                const task = getTask(context, event);

                if (task === null) {
                    return context;
                }

                const {
                    value: { output },
                } = event;

                const nextTask = {
                    ...task,
                    progress: 100,
                    success: true,
                    done: true,
                };

                if (output) {
                    // merge incoming `output` with current `output`
                    nextTask.output = {
                        ...task.output,
                        ...output,
                    };
                }

                if (task.onTaskUpdate) {
                    task.onTaskUpdate(nextTask);
                }

                context.tasks.set(task.taskId, nextTask);

                return context;
            }),
            /**
             * Set progress for a task and update its output
             */
            progress: assign((context: TaskContext, event: TaskEvent) => {
                if (event.type !== 'PROGRESS') {
                    return context;
                }

                const task = getTask(context, event);

                if (task === null) {
                    return context;
                }

                const {
                    value: { output, progress },
                } = event;

                // Do not move progress backwards
                if (task.progress > progress) {
                    return context;
                }

                const nextTask = {
                    ...task,
                    progress,
                };

                if (output) {
                    // merge incoming `output` with current `output`
                    nextTask.output = {
                        ...task.output,
                        ...output,
                    };
                }

                if (task.onTaskUpdate) {
                    task.onTaskUpdate(nextTask);
                }

                context.tasks.set(task.taskId, nextTask);

                return context;
            }),
            /**
             * Set status for a task and update its output
             */
            status: assign((context: TaskContext, event: TaskEvent) => {
                if (event.type !== 'STATUS') {
                    return context;
                }

                const task = getTask(context, event);

                if (task === null) {
                    return context;
                }

                const {
                    value: { output, status },
                } = event;

                const nextTask = {
                    ...task,
                    status,
                };

                if (output) {
                    // merge incoming `output` with current `output`
                    nextTask.output = {
                        ...task.output,
                        ...output,
                    };
                }

                if (task.onTaskUpdate) {
                    task.onTaskUpdate(nextTask);
                }

                context.tasks.set(task.taskId, nextTask);

                return context;
            }),
            /**
             * Cancel and delete all tasks
             */
            cancelAllTasks: assign((context: TaskContext) => {
                context.tasks.forEach((task: UnknownTask): void => {
                    const nextTask = {
                        ...task,
                        done: true,
                        canceled: true,
                    };

                    if (task.onTaskCancel) {
                        task.onTaskCancel(nextTask);
                    }
                });

                return {
                    tasks: new Map(),
                };
            }),
            /**
             * Check if all tasks in its group are either done (due to success or cancellation) or
             * paused (due to an error) and respond accordingly. If we detect that the entire group
             * stopped, then call the `onGroupStopped` callback and delete each of its tasks from
             * context.tasks.
             */
            checkIfGroupStopped: assign(
                (context: TaskContext, event: TaskEvent) => {
                    const task = getTask(context, event);
                    const groupId = task?.groupId ?? null;
                    const onGroupStopped = task?.onGroupStopped ?? null;

                    if (task === null || groupId === null) {
                        return context;
                    }

                    const onGroupStoppedInput: OnGroupStoppedInput<
                        Record<string, unknown>,
                        Record<string, unknown>
                    > = {
                        groupId,
                        success: [] as UnknownTask[],
                        canceled: [] as UnknownTask[],
                        error: [] as UnknownTask[],
                    };

                    let groupStopped = true;

                    const tasks = Array.from(context.tasks.values());

                    for (const currentTask of tasks) {
                        const { canceled, done, error, paused, success } =
                            currentTask;
                        const taskStopped = done || paused;

                        if (!taskStopped) {
                            groupStopped = false;
                            break;
                        }

                        // Even though a task may have an error *and* be canceled, we only want each
                        // task to appear in one of the returned arrays here.
                        if (success) {
                            onGroupStoppedInput.success.push(currentTask);
                        } else if (canceled) {
                            onGroupStoppedInput.canceled.push(currentTask);
                        } else if (error) {
                            onGroupStoppedInput.error.push(currentTask);
                        }
                    }

                    // If the entire group stopped
                    if (groupStopped) {
                        // And the task has an `onGroupStopped` method, call it
                        if (onGroupStopped) {
                            onGroupStopped(onGroupStoppedInput);
                        }

                        // Delete each of the tasks
                        tasks.forEach(({ taskId }: UnknownTask): void => {
                            context.tasks.delete(taskId);
                        });
                    }

                    return context;
                },
            ),
        },
    },
);
